import { useNavigate, useParams } from "#routers/hooks";
import { useIsFeatureAvailable } from "#src/contexts/AuthenticatedContext.helpers";
import useLocalization from "#src/hooks/useLocalization";
import { EquipmentCreateRoute } from "#src/routes/organization/equipment";
import { FacilitiesCreateRoute } from "#src/routes/organization/facilities";
import {
  Button,
  colours,
  DropdownMenu,
  DropdownMenuOption,
  Icon,
  type IconProps,
} from "@validereinc/common-components";
import { AssetType, AssetTypeType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect, useRef, useState } from "react";
import styles from "./CreateAssetDropdownMenu.module.scss";
import { NetworkCreateRoute } from "#src/routes/organization/networks/create";
import { DevicesCreateRoutePath } from "#src/routes/organization/devices/create";
import { FlowCreateRoute } from "#src/routes/organization/flows/create";

const cx = classNames.bind(styles);

export const AssetCreateMode = {
  ASSET_BUILDER: "asset-builder",
  QUICK_CREATE: "quick-create",
} as const;
export type AssetCreateModeType =
  (typeof AssetCreateMode)[keyof typeof AssetCreateMode];

export const CreateAssetDropdownMenu = ({
  shouldUseLightVariant,
  assetType,
}: {
  shouldUseLightVariant?: boolean;
  assetType: AssetTypeType;
}) => {
  const navigate = useNavigate();
  const { facilityId } = useParams<{ facilityId?: string }>();
  const { localize } = useLocalization();
  const [areTemplatedConfigurationsAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "core:templated_configurations",
  });
  const createAssetButtonRef = useRef<HTMLButtonElement | null>(null);
  const [createAssetButtonWidth, setCreateAssetButtonWidth] = useState(0);

  const handleNavigation = ({ mode }: { mode: AssetCreateModeType }) => {
    // TODO: complete the other cases once the paths have been configured
    // in the tickets within the epic CHB-6179
    switch (assetType) {
      case AssetType.FACILITY:
        navigate({
          pathname: FacilitiesCreateRoute.toLink({
            pathParams: { mode },
          }),
        });
        break;
      case AssetType.EQUIPMENT:
        navigate({
          pathname: EquipmentCreateRoute.toLink({
            pathParams: { mode },
          }),
          query: facilityId ? { facilityId } : {},
        });
        break;
      case AssetType.FLOW:
        navigate({
          pathname: FlowCreateRoute.toLink({
            pathParams: { mode },
          }),
        });
        break;
      case AssetType.ASSET_GROUP:
        navigate({
          pathname: NetworkCreateRoute.toLink({
            pathParams: { mode },
          }),
        });
        break;
      case AssetType.DEVICE:
        navigate({
          pathname: DevicesCreateRoutePath.toLink({
            pathParams: { mode },
          }),
          query: facilityId ? { facilityId } : {},
        });
        break;
      default:
        console.error(
          `No Asset Builder or Quick Create page for assetType=${assetType}`
        );
        break;
    }
  };

  const createAssetOptions: DropdownMenuOption[] = [
    ...(areTemplatedConfigurationsAvailable &&
    // TODO: remove this once templated configurations are created for devices and networks
    // i.e. CHB-6663, CHB-6664
    assetType !== AssetType.DEVICE &&
    assetType !== AssetType.ASSET_GROUP
      ? [
          {
            label: "Asset Builder",
            onClick: () => {
              handleNavigation({ mode: AssetCreateMode.ASSET_BUILDER });
            },
            slotLeft: ({ iconProps }: { iconProps: Partial<IconProps> }) => (
              <Icon
                variant="sparkle"
                colour={colours.colours.navy["300"]}
                weight="fill"
                {...iconProps}
              />
            ),
          },
        ]
      : []),
    {
      label: "Quick Create",
      onClick: () => {
        handleNavigation({ mode: AssetCreateMode.QUICK_CREATE });
      },
      slotLeft: ({ iconProps }) => (
        <Icon
          variant="plus-circle"
          {...iconProps}
        />
      ),
    },
  ];

  // We need to programatically get the Create Asset button width as its text can vary.
  // We need to pass it as a style for the DropdownMenu so the menu is the same width as the button.
  useEffect(() => {
    const measureCreateAssetButtonWidth = () => {
      if (createAssetButtonRef.current) {
        setCreateAssetButtonWidth(createAssetButtonRef.current.offsetWidth);
      }
    };
    measureCreateAssetButtonWidth();
  }, []);

  return (
    <DropdownMenu
      contentProps={{
        style: { width: `${createAssetButtonWidth}px` },
      }}
      options={createAssetOptions}
    >
      <Button
        ref={createAssetButtonRef}
        data-testid="create-asset-button"
        className={cx("create-asset-button", {
          "light-variant": shouldUseLightVariant,
        })}
        variant={shouldUseLightVariant ? "outline" : "primary"}
        slotRight={
          <Icon
            variant="triangle-down"
            size={12}
            colour={
              shouldUseLightVariant
                ? colours.colours.text["600"]
                : colours.colours.text["000"]
            }
          />
        }
        slotLeft={
          <Icon
            variant="plus-circle"
            size={24}
          />
        }
      >
        Create {localize(assetType)}
      </Button>
    </DropdownMenu>
  );
};
